// File: frontend/src/pages/Capabilities/components/CapabilityMatrix.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Minus, Info } from 'lucide-react';
import styles from './CapabilityMatrix.module.css';

const CapabilityMatrix = ({ categories }) => {
  const [expandedDetails, setExpandedDetails] = useState(null);
  const versions = ['AI1', 'AI2', 'AI3', 'AI4'];

  const toggleDetails = (categoryIndex, featureIndex) => {
    const key = `${categoryIndex}-${featureIndex}`;
    setExpandedDetails(expandedDetails === key ? null : key);
  };

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th className={styles.headerCell}>Capability</th>
              {versions.map(version => (
                <th key={version} className={styles.headerCell}>{version}</th>
              ))}
              <th className={styles.headerCell}>Details</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, categoryIndex) => (
              <React.Fragment key={category.name}>
                <tr className={styles.categoryRow}>
                  <td 
                    colSpan={versions.length + 2} 
                    className={styles.categoryCell}
                  >
                    {category.name}
                  </td>
                </tr>
                {category.features.map((feature, featureIndex) => (
                  <React.Fragment key={`${categoryIndex}-${featureIndex}`}>
                    <tr 
                      className={styles.featureRow}
                      aria-label={`Feature: ${feature.name}`}
                    >
                      <td className={styles.featureCell}>{feature.name}</td>
                      {versions.map((version, versionIndex) => {
                        const versionKey = `ai${versionIndex + 1}`;
                        return (
                          <td 
                            key={versionKey} 
                            className={styles.valueCell}
                          >
                            {feature[versionKey] ? (
                              <Check 
                                className={styles.checkIcon} 
                                aria-label="Available"
                              />
                            ) : (
                              <Minus 
                                className={styles.minusIcon} 
                                aria-label="Not available"
                              />
                            )}
                          </td>
                        );
                      })}
                      <td className={styles.detailsCell}>
                        <button 
                          className={styles.detailsButton}
                          onClick={() => toggleDetails(categoryIndex, featureIndex)}
                          aria-label="Show details"
                          aria-expanded={expandedDetails === `${categoryIndex}-${featureIndex}`}
                        >
                          <Info size={16} />
                        </button>
                      </td>
                    </tr>
                    <AnimatePresence>
                      {expandedDetails === `${categoryIndex}-${featureIndex}` && (
                        <motion.tr
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className={styles.detailsRow}
                        >
                          <td colSpan={versions.length + 2} className={styles.detailsContent}>
                            <div className={styles.detailsGrid}>
                              {versions.map((version, versionIndex) => {
                                const versionKey = `ai${versionIndex + 1}`;
                                return (
                                  <div key={versionKey} className={styles.detailsItem}>
                                    <h4 className={styles.detailsVersion}>{version}</h4>
                                    <p className={styles.detailsText}>
                                      {feature.details[versionKey]}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

CapabilityMatrix.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          ai1: PropTypes.bool.isRequired,
          ai2: PropTypes.bool.isRequired,
          ai3: PropTypes.bool.isRequired,
          ai4: PropTypes.bool.isRequired,
          details: PropTypes.object.isRequired
        })
      ).isRequired,
    })
  ).isRequired,
};

export default CapabilityMatrix;